import { useEffect } from 'react';

/**
 * Enforces blur/backdrop on gated content even if styles are toggled in DevTools.
 * - Re-applies critical styles with !important
 * - Watches for attribute/class/subtree changes and restores overlay/content styles
 */
export default function useAntiTamperBlur(contentNodeRef, overlayNodeRef, options = {}) {
  const { blurPx = 5, enforceIntervalMs = 800 } = options;

  useEffect(() => {
    const contentEl = contentNodeRef?.current || null;
    const overlayEl = overlayNodeRef?.current || null;
    if (!contentEl && !overlayEl) return undefined;

    const enforceContentStyles = () => {
      if (!contentEl) return;
      const style = getComputedStyle(contentEl);
      const hasBlur = (style.filter || '').includes('blur');
      if (!hasBlur) {
        try {
          contentEl.style.setProperty('filter', `blur(${blurPx}px)`, 'important');
        } catch (_) {
          /* ignore */
        }
      }
      try {
        contentEl.style.setProperty('user-select', 'none', 'important');
        contentEl.style.setProperty('pointer-events', 'none', 'important');
        contentEl.style.setProperty('opacity', '0.85', 'important');
      } catch (_) {
        /* ignore */
      }
    };

    const enforceOverlayStyles = () => {
      if (!overlayEl) return;
      const s = getComputedStyle(overlayEl);
      const needsBackdrop = !(s.backdropFilter || '').includes('blur');
      if (needsBackdrop) {
        try {
          overlayEl.style.setProperty('backdrop-filter', `blur(${blurPx}px)`, 'important');
          overlayEl.style.setProperty('-webkit-backdrop-filter', `blur(${blurPx}px)`, 'important');
        } catch (_) {
          /* ignore */
        }
      }
      try {
        overlayEl.style.setProperty('background-color', 'rgba(255,255,255,0.6)', 'important');
        overlayEl.style.setProperty('display', 'flex', 'important');
        overlayEl.style.setProperty('inset', '0', 'important');
        overlayEl.style.setProperty('position', 'absolute', 'important');
      } catch (_) {
        /* ignore */
      }
    };

    const restore = () => {
      enforceContentStyles();
      enforceOverlayStyles();
    };

    // Initial enforce
    restore();

    const moTargets = [contentEl, overlayEl].filter(Boolean);
    const observers = moTargets.map((el) => {
      const obs = new MutationObserver(() => restore());
      try {
        obs.observe(el, { attributes: true, attributeFilter: ['style', 'class'], subtree: false, childList: false });
      } catch (_) {
        /* ignore */
      }
      return obs;
    });

    // Fallback timer in case DevTools removes the elements entirely
    const intervalId = window.setInterval(() => restore(), enforceIntervalMs);

    return () => {
      observers.forEach((obs) => {
        try {
          obs.disconnect();
        } catch (_) {
          /* ignore */
        }
      });
      window.clearInterval(intervalId);
    };
  }, [contentNodeRef, overlayNodeRef, blurPx, enforceIntervalMs]);
}
